import {
  FC,
  ReactNode,
  RefObject,
  useRef,
  useEffect,
  useContext,
  forwardRef,
} from 'react';
import cn from 'classnames';
import {
  useDrag,
  useDrop,
  DragSourceMonitor,
  DropTargetMonitor,
} from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { ItemTypes, DragItem } from '../../components/DragLayer/DragItemTypes';
import useStyles from './GridItem.styles';
import GridContext from './GridContext';

export interface GridItemDraggableProps {
  nodeId: string;
  onDrop?: (nodeIds: string[]) => void;
  onCanDrop?: (nodeIds: string[]) => boolean;
  children: (ref: (ref: RefObject<any>) => void) => ReactNode;
}

export const GridItemDraggable: FC<GridItemDraggableProps> = (
  { nodeId, onDrop, onCanDrop = () => true, children },
  ref,
) => {
  const classes = useStyles();

  const rootRef = useRef<HTMLElement | null>(null);
  const itemRef = useRef<HTMLElement | null>(null);

  const {
    gridItemClassName,
    getDragStack,
    isDraggingGlobal,
    onDragStart,
    onDragEnd,
    addNodeToNodeMap,
    removeNodeFromNodeMap,
  } = useContext(GridContext);

  // Add grid item to parent node map.
  useEffect(() => {
    if (!rootRef.current) return;
    addNodeToNodeMap(nodeId, rootRef.current);
    return () => removeNodeFromNodeMap(nodeId);
  }, [addNodeToNodeMap, removeNodeFromNodeMap, nodeId]);

  const [{ dragStack }, dragRef, dragPreview] = useDrag({
    item: {
      type: ItemTypes.GridItem,
      stack: [],
      boundingRect: { height: 0, width: 0, x: 0, y: 0 },
    },
    begin: () => {
      onDragStart();

      // // Ensure the node that is being dragged is first in the drag stack.
      // const stack = [nodeId, ...getSelected().filter(id => id !== nodeId)];
      const { height, width, x, y } = itemRef.current
        ? itemRef.current.getBoundingClientRect()
        : { height: 0, width: 0, x: 0, y: 0 };

      return {
        type: ItemTypes.GridItem,
        stack: getDragStack(nodeId),
        boundingRect: { height, width, x, y },
      };
    },
    end: () => {
      onDragEnd();
    },
    collect: (monitor: DragSourceMonitor) => {
      const item: DragItem | null = monitor.getItem();
      return {
        dragStack: item ? item.stack : [],
      };
    },
  });

  // Disable the HTML5 drag preview image becuase we are rendering our
  // own custom drag layer.
  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  const [{ isOver }, dropRef] = useDrop({
    accept: Object.values(ItemTypes),
    drop: (item: DragItem) => {
      if (onDrop && isOver) {
        onDrop(item.stack);
      }
    },
    collect: (monitor: DropTargetMonitor) => {
      const item = monitor.getItem() as DragItem | null;
      if (!item) return { isOver: false };
      return {
        isOver: monitor.isOver({ shallow: true }) && onCanDrop(item.stack),
      };
    },
  });

  const isDragging = isDraggingGlobal && dragStack.includes(nodeId);

  const combinedRef = (innerRef: RefObject<any>) => {
    dragRef(innerRef);
    if (onDrop) {
      dropRef(innerRef);
    }
    itemRef.current = innerRef as any;
  };

  return (
    <div
      ref={(innerRef) => {
        rootRef.current = innerRef;
        if (ref) {
          ref.current = innerRef;
        }
      }}
      className={cn(
        gridItemClassName,
        isDragging && classes.isDragging,
        isOver && classes.isOver,
      )}
    >
      {children(combinedRef)}
    </div>
  );
};

// @ts-ignore
export default forwardRef(GridItemDraggable);
